import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { Video, VideoOff, ScanBarcode, Clock, Package, Play, Download, History, CheckCircle2, Loader2, Cloud, CloudOff, WifiOff, UploadCloud, Trash2 } from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';
import { savePendingRecording, getPendingCount, getPendingRecordingIds, getRecordingById, getCachedData, cacheData, deletePendingRecording } from '../utils/offlineDB';
import { withRecordingSyncLock } from '../utils/syncService';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PackingStation = () => {
  const { user, token } = useAuth();
  const [trackingId, setTrackingId] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingId, setRecordingId] = useState(null);
  const [currentTrackingId, setCurrentTrackingId] = useState('');
  const [elapsedTime, setElapsedTime] = useState(0);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(null); // null = idle, 0-100 = uploading
  const [isManualSyncing, setIsManualSyncing] = useState(false);
  const [coordinates, setCoordinates] = useState(null);

  // Scanned history
  const [scannedHistory, setScannedHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(true);

  // Video playback modal
  const [videoModal, setVideoModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);

  const videoRef = useRef(null);
  const recordingPreviewRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const inputRef = useRef(null);
  const timerRef = useRef(null);
  const lastSyncRef = useRef(0);
  const isSyncingRef = useRef(false);
  const canvasRef = useRef(null);
  const canvasStreamRef = useRef(null);
  const overlayVideoRef = useRef(null);
  const animationRef = useRef(null);
  const historyPollingRef = useRef(null);

  const api = useMemo(() => axios.create({
    baseURL: `${API_URL}/api`,
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);





  const getLocation = () => {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoordinates({
          lat: pos.coords.latitude.toFixed(5),
          lng: pos.coords.longitude.toFixed(5)
        });
      },
      (err) => {
        console.warn("Location error", err);
      },
      { enableHighAccuracy: true }
    );
  };

  const startCanvasOverlay = (trackingId) => {
    const canvas = canvasRef.current;

    // Release any leftover overlay video / canvas stream from a previous
    // recording before creating new ones, so GPU resources don't pile up
    // across a shift (a contributor to "Aw, Snap! STATUS_BREAKPOINT" crashes).
    if (overlayVideoRef.current) {
      overlayVideoRef.current.pause();
      overlayVideoRef.current.srcObject = null;
      overlayVideoRef.current = null;
    }
    if (canvasStreamRef.current) {
      canvasStreamRef.current.getTracks().forEach((track) => track.stop());
      canvasStreamRef.current = null;
    }

    const video = document.createElement("video");
    overlayVideoRef.current = video;

    video.srcObject = streamRef.current;
    video.muted = true;
    video.playsInline = true;

    video.onloadedmetadata = () => {
      video.play();

      // Cap the recording resolution — keeps the per-frame draw cost low
      // now that the canvas is forced to software rendering below.
      const MAX_WIDTH = 640;
      const MAX_HEIGHT = 480;
      const srcWidth = video.videoWidth || 1280;
      const srcHeight = video.videoHeight || 720;
      const scale = Math.min(1, MAX_WIDTH / srcWidth, MAX_HEIGHT / srcHeight);

      canvas.width = Math.round(srcWidth * scale);
      canvas.height = Math.round(srcHeight * scale);

      // Force software (CPU) rendering for this canvas. GPU-composited
      // canvas + simultaneous camera decode + VP9 encode is what crashes
      // the GPU process (STATUS_BREAKPOINT) on weaker laptop GPUs.
      const ctx = canvas.getContext("2d", { willReadFrequently: true });

      const draw = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        // timestamp with milliseconds
        const now = new Date();
        const timestamp =
          now.toLocaleDateString() +
          " " +
          now.toLocaleTimeString() +
          "." +
          now.getMilliseconds().toString().padStart(3, "0");

        // const deviceId = localStorage.getItem("snd_vms_camera") || "CAM-01";

        const lines = [
          // "StarAndDaisy Packing Verification",
          `Time: ${timestamp}`,
          `Packer: ${user.name}`,
          `Tracking: ${trackingId}`,
          // `GPS: ${coordinates ? coordinates.lat + ", " + coordinates.lng : "Locating..."}`,
          // `Device: ${deviceId.slice(0, 20)}`
        ];

        // overlay size
        const boxWidth = 200;
        const boxHeight = 60;

        // position top-left
        const x = 0;
        const y = 0;

        // background box
        ctx.fillStyle = "rgba(0,0,0,0.35)";
        ctx.fillRect(x, y, boxWidth, boxHeight);

        // title
        ctx.fillStyle = "#ffffff";
        ctx.font = "11px system-ui";
        ctx.fillText(lines[0], 8, 16);

        // info text
        ctx.fillStyle = "#ffffff";
        ctx.font = "11px system-ui";

        lines.slice(1).forEach((line, i) => {
          ctx.fillText(line, 8, 34 + i * 14);
        });

        animationRef.current = requestAnimationFrame(draw);
      };

      draw();

      // Lower capture fps reduces encode load on weaker GPUs/CPUs.
      canvasStreamRef.current = canvas.captureStream(15);
    };
  };

  const stopCanvasOverlay = () => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
  };

  // Update pending count
  const updatePendingCount = useCallback(async () => {
    try {
      const count = await getPendingCount();
      setPendingCount(count);
    } catch (e) {
      console.error('Failed to get pending count:', e);
    }
  }, []);

  useEffect(() => {
    getLocation();
  }, []);

  useEffect(() => {
    updatePendingCount();
    const interval = setInterval(updatePendingCount, 10000);
    return () => clearInterval(interval);
  }, [updatePendingCount]);

  // Fetch today's recordings for this executive
  const fetchHistory = useCallback(async () => {
    try {
      if (navigator.onLine) {
        const res = await api.get('/recordings/today');
        // Filter by current user
        const myRecordings = res.data.recordings.filter(r => r.executiveId === user.id);
        setScannedHistory(myRecordings);
        // Cache for offline use
        await cacheData(`history_${user.id}`, myRecordings);
      } else {
        // Load from cache when offline
        const cached = await getCachedData(`history_${user.id}`);
        if (cached) {
          setScannedHistory(cached);
        }
      }
    } catch (err) {
      console.error('Failed to fetch history:', err);
      // Try loading from cache on error
      try {
        const cached = await getCachedData(`history_${user.id}`);
        if (cached) {
          setScannedHistory(cached);
        }
      } catch (e) {
        console.error('Failed to load cached history:', e);
      }
    }
  }, [user?.id, api]);

  // Initialize camera
  const initCamera = useCallback(async () => {
    try {
      const deviceId = localStorage.getItem('snd_vms_camera');
      const constraints = {
        video: deviceId ? { deviceId: { exact: deviceId } } : { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      if (recordingPreviewRef.current) {
        recordingPreviewRef.current.srcObject = stream;
      }
      setCameraReady(true);
      setCameraError('');
    } catch (err) {
      console.error('Camera error:', err);
      setCameraError('Failed to access camera. Please check permissions.');
      setCameraReady(false);
    }
  }, []);

  useEffect(() => {
    initCamera();
    fetchHistory();

    // Poll for history updates every 5 seconds to get compression/upload status
    historyPollingRef.current = setInterval(fetchHistory, 5000);

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (timerRef.current) clearInterval(timerRef.current);
      if (historyPollingRef.current) clearInterval(historyPollingRef.current);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (canvasStreamRef.current) {
        canvasStreamRef.current.getTracks().forEach(track => track.stop());
      }
      if (overlayVideoRef.current) {
        overlayVideoRef.current.pause();
        overlayVideoRef.current.srcObject = null;
      }
    };
  }, [initCamera, fetchHistory]);

  // Focus input on mount and after operations
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [isRecording]);

  const startRecording = async (tid) => {
    if (!cameraReady || !streamRef.current) {
      toast.error('Camera not ready');
      return;
    }

    try {
      // Always use a local ID — no server call needed at start
      const localId = `local-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      setRecordingId(localId);
      setCurrentTrackingId(tid);

      // Set recording state first so preview element renders
      setIsRecording(true);
      setElapsedTime(0);

      // Small delay to ensure preview element is mounted before assigning stream
      await new Promise(resolve => setTimeout(resolve, 100));

      // Assign stream to preview element
      if (recordingPreviewRef.current && streamRef.current) {
        recordingPreviewRef.current.srcObject = streamRef.current;
      }

      chunksRef.current = [];
      startCanvasOverlay(tid);

      await new Promise(resolve => setTimeout(resolve, 300));

      // VP8 is far lighter to encode than VP9 and avoids the GPU-driver
      // crashes (Aw, Snap! STATUS_BREAKPOINT) seen on weaker laptop GPUs.
      let mediaRecorder;
      try {
        mediaRecorder = new MediaRecorder(canvasStreamRef.current, {
          mimeType: "video/webm;codecs=vp8",
          videoBitsPerSecond: 500000
        });
      } catch {
        mediaRecorder = new MediaRecorder(canvasStreamRef.current);
      }

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(1000);

      timerRef.current = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);

      if (!navigator.onLine) {
        toast.warning(`Recording started offline for ${tid}`);
      } else {
        toast.success(`Recording started for ${tid}`);
      }
    } catch (err) {
      setIsRecording(false);
      toast.error(err.response?.data?.error || 'Failed to start recording');
    }
  };

  const stopRecording = async () => {
    if (!mediaRecorderRef.current || !recordingId) return;

    mediaRecorderRef.current.stop();
    clearInterval(timerRef.current);

    // Capture values before state resets
    const tid = currentTrackingId;
    const duration = elapsedTime;
    const startTime = new Date(Date.now() - elapsedTime * 1000).toISOString();

    setIsRecording(false);
    setRecordingId(null);
    setCurrentTrackingId('');
    setElapsedTime(0);
    setTrackingId('');
    if (inputRef.current) inputRef.current.focus();
    stopCanvasOverlay();
    await new Promise(resolve => setTimeout(resolve, 500));
    const blob = new Blob(chunksRef.current, { type: 'video/webm' });

    if (navigator.onLine) {
      try {
        // 1. Get a SAS URL from backend for direct Azure upload
        const { data: { sasUrl, blobName, cloudUrl } } = await api.get(
          `/recordings/upload-url?trackingId=${encodeURIComponent(tid)}`
        );

        // 2. Upload directly to Azure with progress tracking
        setUploadProgress(0);
        toast.info(`Uploading ${tid} to cloud…`);

        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('PUT', sasUrl);
          xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
          xhr.setRequestHeader('Content-Type', 'video/webm');
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 100));
          };
          xhr.onload = () => xhr.status < 300 ? resolve() : reject(new Error(`Upload failed: ${xhr.status}`));
          xhr.onerror = () => reject(new Error('Upload failed'));
          xhr.send(blob);
        });

        // 3. Notify backend to save metadata in MongoDB
        await api.post('/recordings/confirm-upload', {
          trackingId: tid,
          blobName,
          cloudUrl,
          duration,
          startTime,
          fileSize: blob.size,
        });

        setUploadProgress(null);
        toast.success(`Recording saved for ${tid}`);
        fetchHistory();
      } catch (err) {
        setUploadProgress(null);
        console.error('Azure upload failed, saving locally:', err);

        // Fallback: save to IndexedDB for later sync
        try {
          const arrayBuffer = await blob.arrayBuffer();
          await savePendingRecording({
            trackingId: tid,
            serverId: null,
            executiveId: user.id,
            executiveName: user.name,
            duration,
            videoBlob: arrayBuffer,
            startTime,
          });
          await updatePendingCount();
          toast.warning(`Upload failed. Saved locally — will retry when online.`);
        } catch (localErr) {
          toast.error('Failed to save recording');
          console.error('Local save error:', localErr);
        }
      }
    } else {
      // Offline — save to IndexedDB, sync service will upload when back online
      try {
        const arrayBuffer = await blob.arrayBuffer();
        await savePendingRecording({
          trackingId: tid,
          serverId: null,
          executiveId: user.id,
          executiveName: user.name,
          duration,
          videoBlob: arrayBuffer,
          startTime,
        });
        await updatePendingCount();
        toast.success(`Recording saved locally for ${tid}. Will upload when online.`);
      } catch (localErr) {
        toast.error('Failed to save recording locally');
        console.error('Local save error:', localErr);
      }
    }
  };

  const handleScan = async (e) => {
    e.preventDefault();
    const scannedId = trackingId.trim();
    if (!scannedId) return;

    if (!isRecording) {
      await startRecording(scannedId);
    } else if (scannedId === currentTrackingId) {
      await stopRecording();
    } else {
      toast.error('Please scan the same tracking ID to stop recording');
      setTrackingId('');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '—';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const deleteVideo = async (recording) => {
    try {
      await api.delete(`/recordings/${recording.id}`);
      setScannedHistory(prev => prev.filter(r => r.id !== recording.id));
      toast.success(`Deleted: ${recording.trackingId}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
    }
  };

  const playVideo = async (recording) => {
    if (!recording.filename) {
      toast.error('Video not available');
      return;
    }
    try {
      const { data } = await api.get(`/recordings/${recording.id}/play-url`);
      const playUrl = data.source === 'local'
        ? `${API_URL}${data.playUrl}`
        : data.playUrl;
      setSelectedVideo({ ...recording, playUrl });
      setVideoModal(true);
    } catch (err) {
      toast.error('Failed to load video');
    }
  };

  const downloadVideo = async (recording) => {
    if (!recording.filename) {
      toast.error('Video file not available');
      return;
    }
    try {
      const { data } = await api.get(`/recordings/${recording.id}/play-url`);
      const url = data.source === 'local'
        ? `${API_URL}${data.playUrl}`
        : data.playUrl;
      const a = document.createElement('a');
      a.href = url;
      a.download = `${recording.trackingId}-${format(new Date(recording.createdAt), 'yyyy-MM-dd')}.webm`;
      a.click();
      toast.success('Download started');
    } catch (err) {
      toast.error('Failed to download video');
    }
  };

  // Get status badge with progress
  const getStatusBadge = (rec) => {
    const status = rec.status;

    if (status === 'compressing') {
      return (
        <div className="flex items-center gap-1.5" data-testid={`status-compressing-${rec.id}`}>
          <Loader2 className="w-3 h-3 animate-spin text-yellow-400" />
          <span className="text-xs text-yellow-400">{rec.compressionProgress || 0}%</span>
        </div>
      );
    }

    if (status === 'uploading') {
      return (
        <div className="flex items-center gap-1.5" data-testid={`status-uploading-${rec.id}`}>
          <Cloud className="w-3 h-3 animate-pulse text-blue-400" />
          <span className="text-xs text-blue-400">{rec.uploadProgress || 0}%</span>
        </div>
      );
    }

    if (status === 'uploaded') {
      return (
        <div className="flex items-center gap-1" title="Uploaded to Azure" data-testid={`status-uploaded-${rec.id}`}>
          <Cloud className="w-3 h-3 text-green-400" />
          <CheckCircle2 className="w-3 h-3 text-green-500" />
        </div>
      );
    }

    if (status === 'compressed') {
      return (
        <div className="flex items-center gap-1" title="Compressed, pending upload" data-testid={`status-compressed-${rec.id}`}>
          <CheckCircle2 className="w-3 h-3 text-green-500" />
          <CloudOff className="w-3 h-3 text-zinc-500" />
        </div>
      );
    }

    if (status === 'completed') {
      return <CheckCircle2 className="w-3 h-3 text-green-500" data-testid={`status-completed-${rec.id}`} />;
    }

    if (status === 'error') {
      return <span className="text-xs text-red-400" data-testid={`status-error-${rec.id}`}>Error</span>;
    }

    return null;
  };

  const uploadRecording = async (rec) => {

    const blob = new Blob([rec.videoBlob], { type: 'video/webm' });

    const { data: { sasUrl, blobName, cloudUrl } } =
      await api.get(`/recordings/upload-url?trackingId=${encodeURIComponent(rec.trackingId)}`);

    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.open('PUT', sasUrl);
      xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
      xhr.setRequestHeader('Content-Type', 'video/webm');

      xhr.onload = () => xhr.status < 300 ? resolve() : reject(new Error(`Azure ${xhr.status}`));
      xhr.onerror = () => reject(new Error('Network error'));

      xhr.send(blob);
    });

    await api.post('/recordings/confirm-upload', {
      trackingId: rec.trackingId,
      blobName,
      cloudUrl,
      duration: rec.duration,
      startTime: rec.startTime,
      fileSize: blob.size
    });

  };

  const syncPendingRecordings = useCallback(async () => {
    if (isSyncingRef.current) return;

    isSyncingRef.current = true;

    try {
      // Shared lock: ConnectionStatus and syncService also sync on reconnect
      await withRecordingSyncLock(async () => {
        // Fetch only IDs/metadata first — avoids loading all video blobs into memory at once
        const pendingIds = await getPendingRecordingIds();
        console.log(`[Sync] Found ${pendingIds.length} pending recordings`);

        let synced = 0;
        let failed = 0;

        for (const meta of pendingIds) {
          try {
            // Load one recording's blob at a time
            const rec = await getRecordingById(meta.id);
            if (!rec) { console.warn(`[Sync] Record ${meta.id} not found, skipping`); continue; }

            console.log(`[Sync] Uploading ${rec.trackingId} (id: ${rec.id}, size: ${rec.videoBlob?.byteLength ?? rec.videoBlob?.size ?? 'unknown'} bytes)`);
            await uploadRecording(rec);
            await deletePendingRecording(rec.id);
            synced++;
            console.log(`[Sync] ✅ Done ${rec.trackingId} (${synced}/${pendingIds.length})`);
          } catch (err) {
            const status = err?.response?.status;
            const errData = err?.response?.data || err?.message || err;

            if (status === 409 || status === 400) {
              // 409 = already on server | 400 = bad data (e.g. duration<5s) — will never succeed
              console.warn(`[Sync] ${status} removing ${meta.trackingId} from local DB`);
              await deletePendingRecording(meta.id);
              synced++;
            } else {
              // Temporary failure (403, 5xx, network) — leave in DB, retry next cycle
              failed++;
              console.error(`[Sync] Failed ${meta.trackingId} (${status ?? 'network'}):`, errData);
            }
          }
        }

        console.log(`[Sync] Complete. Synced: ${synced}, Failed: ${failed}`);
      });
      updatePendingCount();
      fetchHistory();

    } catch (err) {
      console.error('[Sync] Unexpected error:', err);
    } finally {
      isSyncingRef.current = false;
    }
  }, [fetchHistory, updatePendingCount]);

  const handleManualSync = useCallback(async () => {
    if (isManualSyncing) return;
    if (!navigator.onLine) {
      toast.error('No network connection. Please check your connection and try again.');
      return;
    }
    // Force-reset stuck sync lock before manual trigger
    isSyncingRef.current = false;
    setIsManualSyncing(true);
    try {
      const pending = await getPendingRecordingIds();
      if (pending.length === 0) {
        toast.success('No pending videos to upload.');
        return;
      }
      toast.info(`Uploading ${pending.length} pending videos...`);
      await syncPendingRecordings();
      const remaining = await getPendingRecordingIds();
      if (remaining.length === 0) {
        toast.success('All pending videos uploaded successfully!');
      } else {
        toast.warning(`${remaining.length} videos still pending. Check console for errors.`);
      }
    } catch (err) {
      console.error('[ManualSync] Error:', err);
      toast.error('Upload failed. Check console for details.');
    } finally {
      setIsManualSyncing(false);
    }
  }, [isManualSyncing, syncPendingRecordings]);

  useEffect(() => {
    if (navigator.onLine) {
      syncPendingRecordings();
    }
  }, [syncPendingRecordings]);

  // Retry pending uploads every 2 minutes while online
  useEffect(() => {
    const interval = setInterval(() => {
      if (navigator.onLine && !isSyncingRef.current) {
        syncPendingRecordings();
      }
    }, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [syncPendingRecordings]);

  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => {
      const now = Date.now();

      if (now - lastSyncRef.current < 5000) return;

      lastSyncRef.current = now;

      setIsOffline(false);
      toast.success('Back online!');
      fetchHistory();
      syncPendingRecordings();
    };
    const handleOffline = () => {
      setIsOffline(true);
      toast.warning('You are offline. Recordings will be saved locally.');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [fetchHistory, syncPendingRecordings]);

  return (
    <div className="h-full flex bg-zinc-950" data-testid="packing-station">
      {/* Main Content */}
      <div className="flex-1 flex flex-col relative overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0 z-0">
          <canvas
            ref={canvasRef}
            style={{ display: "none" }}
          />
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover opacity-90"
            data-testid="camera-preview"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/50 to-transparent" />
        </div>

        {/* HUD Overlay */}
        <div className="relative z-10 flex-1 flex flex-col p-6 md:p-8">
          {/* Top Bar */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="glass rounded-xl px-4 py-2 flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${cameraReady ? 'bg-green-500' : 'bg-red-500'}`} />
                <span className="text-sm font-medium text-white">
                  {cameraReady ? 'Camera Ready' : 'Camera Offline'}
                </span>
              </div>

              {/* Offline indicator */}
              {isOffline && (
                <div className="glass rounded-xl px-4 py-2 flex items-center gap-2 bg-amber-500/20 border-amber-500/30" data-testid="offline-mode-indicator">
                  <WifiOff className="w-4 h-4 text-amber-400" />
                  <span className="text-sm font-medium text-amber-400">Offline Mode</span>
                </div>
              )}

              {/* Pending sync indicator */}
              {pendingCount > 0 && (
                <button
                  onClick={handleManualSync}
                  disabled={isManualSyncing || isOffline}
                  className="glass rounded-xl px-4 py-2 flex items-center gap-2 bg-blue-500/20 border-blue-500/30 hover:bg-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="pending-sync-indicator"
                  title={isOffline ? 'No network' : 'Click to upload pending videos'}
                >
                  {isManualSyncing
                    ? <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />
                    : <UploadCloud className="w-4 h-4 text-blue-400" />
                  }
                  <span className="text-sm font-medium text-blue-400">
                    {isManualSyncing ? 'Uploading...' : `${pendingCount} pending`}
                  </span>
                </button>
              )}
            </div>

            {isRecording && (
              <div className="glass rounded-xl px-4 py-2 flex items-center gap-3 recording-pulse bg-red-900/50 border-red-500/50" data-testid="recording-indicator">
                <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
                <span className="text-sm font-bold text-red-300 mono">{formatTime(elapsedTime)}</span>
              </div>
            )}

            {uploadProgress !== null && (
              <div className="glass rounded-xl px-4 py-2 flex items-center gap-3 bg-blue-900/50 border-blue-500/50" data-testid="upload-progress-indicator">
                <Cloud className="w-4 h-4 text-blue-400 animate-pulse" />
                <div className="flex items-center gap-2">
                  <div className="w-24 h-1.5 bg-zinc-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-400 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <span className="text-sm font-bold text-blue-300 mono">{uploadProgress}%</span>
                </div>
              </div>
            )}

            <div className="glass rounded-xl px-4 py-2">
              <span className="text-sm text-zinc-400">Executive: </span>
              <span className="text-sm font-medium text-white">{user?.name}</span>
            </div>
          </div>

          {/* Center Content */}
          <div className="flex-1 flex items-center justify-center">
            <div className="flex gap-6 w-full max-w-5xl">
              {/* Main Scan Card */}
              <Card className="flex-1 bg-zinc-900/80 backdrop-blur-xl border-zinc-800 p-8">
                {cameraError ? (
                  <div className="text-center py-8">
                    <VideoOff className="w-16 h-16 text-red-400 mx-auto mb-4" />
                    <p className="text-red-300 mb-4">{cameraError}</p>
                    <Button onClick={initCamera} variant="outline" className="border-zinc-700">
                      Retry Camera
                    </Button>
                  </div>
                ) : (
                  <>
                    {/* Status Display */}
                    <div className="text-center mb-6">
                      {isRecording ? (
                        <>
                          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 border-2 border-red-500 flex items-center justify-center recording-pulse">
                            <Video className="w-8 h-8 text-red-400" />
                          </div>
                          <h2 className="text-xl font-bold text-white mb-2">Recording in Progress</h2>
                          <p className="text-zinc-400">Tracking ID: <span className="text-green-400 font-mono text-lg">{currentTrackingId}</span></p>
                        </>
                      ) : (
                        <>
                          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-indigo-500/20 border-2 border-indigo-500 flex items-center justify-center">
                            <ScanBarcode className="w-8 h-8 text-indigo-400" />
                          </div>
                          <h2 className="text-xl font-bold text-white mb-2">Ready to Scan</h2>
                          <p className="text-zinc-400">Scan tracking ID to start recording</p>
                        </>
                      )}
                    </div>

                    {/* Scan Input */}
                    <form onSubmit={handleScan} className="space-y-4">
                      <div className="relative">
                        <Input
                          ref={inputRef}
                          type="text"
                          value={trackingId}
                          onChange={(e) => setTrackingId(e.target.value)}
                          placeholder={isRecording ? 'Scan same ID to stop...' : 'Scan Tracking ID...'}
                          className="w-full h-14 text-xl text-center font-mono bg-zinc-950 border-2 border-zinc-700 focus:border-green-500 focus:ring-0 placeholder:text-zinc-700"
                          autoFocus
                          data-testid="tracking-id-input"
                        />
                        <div className="absolute inset-0 overflow-hidden rounded-md pointer-events-none">
                          <div className="w-full h-1 bg-gradient-to-b from-transparent via-green-500/50 to-transparent scan-beam" />
                        </div>
                      </div>

                      <div className="flex gap-3">
                        {isRecording ? (
                          <Button
                            type="button"
                            onClick={stopRecording}
                            className="flex-1 h-11 bg-red-600 hover:bg-red-500 text-white font-medium"
                            data-testid="stop-recording-btn"
                            disabled={elapsedTime < 5}
                          >
                            <VideoOff className="w-5 h-5 mr-2" />
                            Stop Recording
                          </Button>
                        ) : (
                          <Button
                            type="submit"
                            disabled={!trackingId.trim() || !cameraReady}
                            className="flex-1 h-11 bg-indigo-600 hover:bg-indigo-500 text-white font-medium disabled:opacity-50"
                            data-testid="start-recording-btn"
                          >
                            <Video className="w-5 h-5 mr-2" />
                            Start Recording
                          </Button>
                        )}
                      </div>
                    </form>

                    {/* Live Recording Preview */}
                    {isRecording && (
                      <div className="mt-6 pt-6 border-t border-zinc-800">
                        <div className="text-sm text-zinc-400 mb-3 flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                          Live Recording Preview
                        </div>
                        <div className="aspect-video rounded-lg overflow-hidden border border-red-500/30 bg-black">
                          <video
                            ref={recordingPreviewRef}
                            autoPlay
                            playsInline
                            muted
                            className="w-full h-full object-cover"
                            data-testid="recording-preview"
                          />
                        </div>
                      </div>
                    )}

                    {/* Instructions */}
                    <div className="mt-6 pt-6 border-t border-zinc-800">
                      <h3 className="text-sm font-medium text-zinc-400 mb-3 uppercase tracking-wider">Instructions</h3>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="flex items-start gap-2">
                          <div className="w-6 h-6 rounded bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                            <span className="text-indigo-400 text-xs font-bold">1</span>
                          </div>
                          <p className="text-xs text-zinc-400">Scan tracking ID</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-6 h-6 rounded bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                            <span className="text-yellow-400 text-xs font-bold">2</span>
                          </div>
                          <p className="text-xs text-zinc-400">Pack & paste label</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-6 h-6 rounded bg-green-500/20 flex items-center justify-center flex-shrink-0">
                            <span className="text-green-400 text-xs font-bold">3</span>
                          </div>
                          <p className="text-xs text-zinc-400">Scan same ID to stop</p>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </Card>

              {/* Scanned History Panel */}
              {showHistory && (
                <Card className="w-80 bg-zinc-900/80 backdrop-blur-xl border-zinc-800 p-4 flex flex-col" data-testid="scanned-history">
                  <div className="flex items-center gap-2 mb-4 pb-3 border-b border-zinc-800">
                    <History className="w-5 h-5 text-indigo-400" />
                    <h3 className="font-semibold text-white">Today's Scans</h3>
                    <span className="ml-auto text-xs text-zinc-500 bg-zinc-800 px-2 py-1 rounded">
                      {scannedHistory.length}
                    </span>
                  </div>

                  <ScrollArea className="flex-1">
                    <div className="space-y-2 pr-2">
                      {scannedHistory.length === 0 ? (
                        <p className="text-zinc-500 text-sm text-center py-8">No scans yet today</p>
                      ) : (
                        scannedHistory.map((rec) => (
                          <div
                            key={rec.id}
                            className="p-3 bg-zinc-950/50 rounded-lg border border-zinc-800/50 hover:border-zinc-700 transition-colors group"
                            data-testid={`history-item-${rec.id}`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-mono text-indigo-400 text-sm">{rec.trackingId}</span>
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => playVideo(rec)}
                                  disabled={!rec.filename || rec.status === 'compressing'}
                                  className="h-6 w-6 p-0 text-zinc-400 hover:text-white"
                                >
                                  <Play className="w-3 h-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => downloadVideo(rec)}
                                  disabled={!rec.filename || rec.status === 'compressing'}
                                  className="h-6 w-6 p-0 text-zinc-400 hover:text-white"
                                >
                                  <Download className="w-3 h-3" />
                                </Button>
                                {user?.role === 'admin' && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => deleteVideo(rec)}
                                    className="h-6 w-6 p-0 text-zinc-400 hover:text-red-400"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                )}
                              </div>
                            </div>

                            {/* Progress Bar for compressing/uploading */}
                            {(rec.status === 'compressing' || rec.status === 'uploading') && (
                              <div className="mb-2">
                                <Progress
                                  value={rec.status === 'compressing' ? rec.compressionProgress : rec.uploadProgress}
                                  className="h-1"
                                />
                              </div>
                            )}

                            <div className="flex items-center gap-3 text-xs text-zinc-500">
                              <span>{format(new Date(rec.createdAt), 'HH:mm')}</span>
                              <span className="font-mono">{formatDuration(rec.duration)}</span>
                              <div className="ml-auto">
                                {getStatusBadge(rec)}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </ScrollArea>

                  {/* Legend */}
                  <div className="mt-3 pt-3 border-t border-zinc-800">
                    <p className="text-xs text-zinc-500 mb-2">Status Legend:</p>
                    <div className="grid grid-cols-2 gap-1 text-xs">
                      <div className="flex items-center gap-1.5 text-zinc-400">
                        <Loader2 className="w-3 h-3 text-yellow-400" />
                        <span>Compressing</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-zinc-400">
                        <Cloud className="w-3 h-3 text-blue-400" />
                        <span>Uploading</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-zinc-400">
                        <CheckCircle2 className="w-3 h-3 text-green-500" />
                        <span>Ready</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-zinc-400">
                        <Cloud className="w-3 h-3 text-green-400" />
                        <span>In Cloud</span>
                      </div>
                    </div>
                  </div>
                </Card>
              )}
            </div>
          </div>

          {/* Bottom Stats */}
          <div className="flex justify-center gap-4 mt-4">
            <div className="glass rounded-xl px-4 py-2 flex items-center gap-2">
              <Clock className="w-4 h-4 text-zinc-400" />
              <span className="text-sm text-zinc-400">{new Date().toLocaleTimeString()}</span>
            </div>
            <div className="glass rounded-xl px-4 py-2 flex items-center gap-2">
              <Package className="w-4 h-4 text-zinc-400" />
              <span className="text-sm text-zinc-400">Packing Station</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowHistory(!showHistory)}
              className={`text-zinc-400 hover:text-white ${showHistory ? 'bg-zinc-800' : ''}`}
              data-testid="toggle-history-btn"
            >
              <History className="w-4 h-4 mr-2" />
              History
            </Button>
          </div>
        </div>
      </div>

      {/* Video Player Modal */}
      <Dialog open={videoModal} onOpenChange={setVideoModal}>
        <DialogContent className="max-w-4xl bg-zinc-900 border-zinc-800" data-testid="video-playback-modal">
          <DialogHeader>
            <DialogTitle className="text-white">
              Recording: <span className="text-indigo-400 font-mono">{selectedVideo?.trackingId}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {selectedVideo?.playUrl && (
              <video
                src={selectedVideo.playUrl}
                controls
                autoPlay
                className="w-full rounded-lg bg-black"
              />
            )}
            <div className="mt-4 grid grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-zinc-500">Time</p>
                <p className="text-zinc-300">{selectedVideo && format(new Date(selectedVideo.createdAt), 'PPp')}</p>
              </div>
              <div>
                <p className="text-zinc-500">Duration</p>
                <p className="text-zinc-300 font-mono">{formatDuration(selectedVideo?.duration)}</p>
              </div>
              <div>
                <p className="text-zinc-500">Status</p>
                <p className="text-green-400 capitalize">{selectedVideo?.status}</p>
              </div>
              <div>
                <p className="text-zinc-500">Compression</p>
                <p className="text-zinc-300">{selectedVideo?.compressionRatio ? `${selectedVideo.compressionRatio}% saved` : '—'}</p>
              </div>
            </div>
            <div className="mt-4">
              <Button onClick={() => downloadVideo(selectedVideo)} className="bg-indigo-600 hover:bg-indigo-500">
                <Download className="w-4 h-4 mr-2" />
                Download Video
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PackingStation;
